/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as ActionTypes from "constants/ActionTypes";
import { BlockToken } from "types/Block";
import gatewayFetch from "utilities/gatewayFetch";
import { mapFromServerFormat } from "utilities/ApiUtilities";
import { saveTranscript } from "utilities/ProjectUtilities";
import { AppAction } from "types/AppAction";
import { Asset, AssetTranscript } from "types/Asset";
import { AssetId, MediaType } from "@phonos/types";
import {
  AssetLoadedAction,
  AssetLoadFailedAction,
  AssetProcessingFailedAction,
  AssetUploadFailedAction,
  AssetVideoProcessingCompletedAction,
  AssetWaveformComputedAction,
  InsertAssetAction,
  RecordingSessionTrackMediaProcessingFailedAction,
  StudioESV2AssetCreatedAction,
  StudioSoundAssetCreatedAction,
  TranscribeAssetEmptyAction,
  TranscribeAssetFailureAction,
} from "types/Actions";
import { BlockTypes } from "constants/BlockTypes";
import { saveProject } from "actions/ProjectActions";
import { editTranscriptFromTokens } from "utilities/TranscriptEditUtilities";
import { setFilter } from "actions/FilterActions";
import FilterTypes, { selectEnhanceSpeechFilter } from "constants/FilterTypes";
import { addToast } from "actions/ToastMessageActions";
import { TranscriptionService } from "constants/TranscriptionService";
import * as ProjectUtilities from "utilities/ProjectUtilities";
import { MediaParserDimensions } from "@remotion/media-parser";

export function studioSoundAssetCreated(
  originalAssetId: AssetId,
  originalAsset: Asset,
  newAssetId: AssetId,
  newAsset: Asset
): StudioSoundAssetCreatedAction {
  return {
    type: ActionTypes.STUDIO_SOUND_ASSET_CREATED,
    originalAssetId,
    originalAsset,
    newAssetId,
    newAsset,
  };
}

export function studioESV2AssetCreated(
  originalAssetId: AssetId,
  originalAsset: Asset
): StudioESV2AssetCreatedAction {
  return {
    type: ActionTypes.STUDIO_ESV2_ASSET_CREATED,
    originalAssetId,
    originalAsset,
  };
}

export function assetProcessingFailed(
  assetId: AssetId
): AssetProcessingFailedAction {
  return {
    type: ActionTypes.ASSET_PROCESSING_FAILED,
    assetId: assetId,
  };
}

export function recordingSessionTrackMediaProcessingFailed(
  assetId: AssetId,
  recordingSessionTrackId: string
): RecordingSessionTrackMediaProcessingFailedAction {
  return {
    type: ActionTypes.RECORDING_SESSION_TRACK_MEDIA_PROCESSING_FAILED,
    assetId,
    recordingSessionTrackId,
  };
}

export function assetVideoProcessingCompleted(
  assetId: AssetId,
  asset: Asset
): AssetVideoProcessingCompletedAction {
  return {
    type: ActionTypes.ASSET_VIDEO_PROCESSING_COMPLETED,
    assetId,
    asset,
  };
}

export function projectEnhancementFailed(): AppAction {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();
    const currentFilter = selectEnhanceSpeechFilter(project.filters);
    if (currentFilter.enabled) {
      dispatch(
        setFilter({
          ...currentFilter,
          filterName: FilterTypes.ENHANCE_SPEECH,
          enabled: false,
        })
      );

      dispatch(addToast({ message: I18n.t("enhancement.error") }));
    }
  };
}

export function transcribeAssetFailed(
  assetId: AssetId
): TranscribeAssetFailureAction {
  return {
    type: ActionTypes.TRANSCRIBE_ASSET_FAILURE,
    assetId,
  };
}

export function transcribeAssetEmpty(
  assetId: AssetId
): TranscribeAssetEmptyAction {
  return {
    type: ActionTypes.TRANSCRIBE_ASSET_EMPTY,
    assetId,
  };
}

export function cleanupFailedAssetUpload(
  assetId: AssetId
): AssetUploadFailedAction {
  return {
    type: ActionTypes.ASSET_UPLOAD_FAILED,
    assetId,
  };
}

interface TranscribeAssetSuccessResponse {
  assetId: AssetId;
  transcript: AssetTranscript;
  transcriptionService: TranscriptionService;
  transcriptDetectedLanguage: string;
}

export function transcribeAssetSuccess(assetId: AssetId): AppAction {
  return async (dispatch, getState): Promise<void> => {
    const { project, clips } = getState();
    const response = await gatewayFetch(
      `/api/v1/projects/${project.id}/tracks/${assetId}`,
      {
        method: "GET",
      }
    );
    // TODO: Actual validations here?
    const data = mapFromServerFormat(
      await response.json()
    ) as TranscribeAssetSuccessResponse;
    dispatch({
      type: ActionTypes.TRANSCRIBE_ASSET_SUCCESS,
      assetId,
      transcript: data.transcript,
      transcriptionService: data.transcriptionService,
      transcriptDetectedLanguage: data.transcriptDetectedLanguage,
    });

    if (data.transcript === null || data.transcript.length === 0) {
      // Convert clips to waveform blocks if there's no transcript (i.e. no speech, or no language detected)
      // This is the same behaviour as a failed transcription.
      dispatch(transcribeAssetEmpty(assetId));
      dispatch(addToast({ message: I18n.t("transcription.error") }));
    }

    // ideally would prefer to hook into the transcription update server side,
    // but our server model doesn't have support for building up an accurate transcript
    // a la `getBlocks`.
    await dispatch(saveProject());
  };
}

export const startAssetWaveformComputation = (
  assetId: AssetId
): AppAction<Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    const { project, assets } = getState();

    const asset = assets[assetId];

    if (!asset || asset.waveformUrl || asset.waveformComputationStarted) {
      return;
    }

    dispatch({
      type: ActionTypes.ASSET_WAVEFORM_COMPUTATION_STARTED,
      assetId,
    });

    await ProjectUtilities.computeTrackWaveform(project, assetId);
  };
};

export const assetWaveformComputed = (
  assetId: AssetId,
  waveformUrl: string
): AssetWaveformComputedAction => {
  return {
    type: ActionTypes.ASSET_WAVEFORM_COMPUTED,
    assetId,
    waveformUrl,
  };
};

export function setAssetTranscriptionToProcessing({
  clipId,
}: {
  clipId: string;
}): AppAction {
  return async (dispatch, getState): Promise<void> => {
    const { clips, assets } = getState();
    const clip = clips.find((clip) => clip.id === clipId);
    if (!clip || clip.type === BlockTypes.PLACEHOLDER) {
      return;
    }

    const assetId = clip.assetId;
    if (!assetId || assets[assetId]?.transcript?.length) {
      return;
    }
    dispatch({
      type: ActionTypes.TRANSCRIBE_ASSET_PROCESS,
      assetId,
      startedTranscriptionAt: new Date(),
    });
  };
}

export const assetLoaded = (assetId: AssetId): AssetLoadedAction => {
  return {
    type: ActionTypes.ASSET_LOADED,
    assetId,
  };
};

export const assetLoadFailed = (assetId: AssetId): AssetLoadFailedAction => {
  return {
    type: ActionTypes.ASSET_LOAD_FAILED,
    assetId,
  };
};

export interface LocalAsset {
  file: File;
  assetId: string;
  duration: number;
  mediaType: MediaType;
  videoDimensions?: MediaParserDimensions;
  videoCodec?: string;
}

interface InsertAssetParams {
  localAsset: LocalAsset;
  clipId: string;
}
export const insertLocalAsset = ({
  localAsset,
  clipId,
}: InsertAssetParams): InsertAssetAction => {
  const { assetId, duration, file, mediaType } = localAsset;
  return {
    type: ActionTypes.INSERT_ASSET,
    asset: {
      id: assetId,
      duration,
      file,
      hasAnyEnhancementErrors: false,
      mediaType,
    },
    clipId: clipId,
    fileName: file.name,
  };
};

export const savedTranscriptVersions: Record<AssetId, string | undefined> = {};

export const updateTranscriptFromTokens = (
  assetId: AssetId,
  tokens: BlockToken[],
  newText: string
): AppAction<Promise<boolean>> => {
  return async (dispatch, getState): Promise<boolean> => {
    const {
      project: { id: projectId },
      assets: {
        [assetId]: { transcript: originalTranscript },
      },
    } = getState();

    const updatedTranscript = editTranscriptFromTokens(
      originalTranscript,
      tokens,
      newText
    );

    if (!updatedTranscript) {
      return false;
    }

    dispatch({
      type: ActionTypes.UPDATE_LOCAL_TRANSCRIPT,
      assetId,
      transcript: updatedTranscript,
    });

    try {
      const {
        assets: {
          [assetId]: { transcriptVersion: updatedTranscriptVersion },
        },
      } = getState();

      const result = await saveTranscript(
        projectId,
        assetId,
        updatedTranscript
      );

      if (result.ok) {
        if (!!updatedTranscriptVersion) {
          savedTranscriptVersions[assetId] = updatedTranscriptVersion;
        }
        return true;
      } else {
        // TODO: Is this worse than duplicating the failure logic in the catch?
        throw new Error(`saveTranscript failed with status: ${result.status}`);
      }
    } catch (error) {
      // Rollback the transcript edit since we couldn't save it
      if (!!originalTranscript) {
        dispatch({
          type: ActionTypes.UPDATE_LOCAL_TRANSCRIPT,
          assetId,
          transcript: originalTranscript,
        });
      }
      console.error(error);
      newrelic.noticeError(error);

      return false;
    }
  };
};
